/**
 * routineStatus — the ONE reading of a routine's lifecycle state (P2, 2026-09-16).
 *
 * THE DEFECT. A `dates` routine that ran its last date was stored as 'paused',
 * and every surface rendered "Paused · [Resume]" — an offer nobody can take,
 * because resuming refuses ("no future dates left on its schedule"). Eleven of
 * the twelve routines on prest0-inc were exactly that: one-shot follow-ups
 * Prest0n had minted inside Miles's own triage threads, every one of them
 * reading as a paused routine he could apparently restart.
 *
 * The server now writes 'completed' for that state. This module is how the
 * client reads it, in one place, because ~eight call sites asked the question
 * as `status === 'paused'` / `status !== 'paused'` — and every one of those
 * would answer "running" for a completed routine, which is the worse error.
 *
 * FIREBASE-FREE by construction (the chat-isolation battery's load-time trap):
 * pure string predicates, no imports.
 */

/** What a routine doc — and the thread mirror's aggregate — may say. */
export type RoutineLifecycleStatus = 'active' | 'paused' | 'completed';

/** Normalize an unknown wire/mirror value; anything unrecognised reads 'active'
 *  (today's behaviour for an absent mirror field, and the safe direction: a
 *  live routine is never hidden by a value this build has not learnt yet). */
export function coerceRoutineStatus(status?: string | null): RoutineLifecycleStatus {
  return status === 'paused' || status === 'completed' ? status : 'active';
}

/** Not running — paused OR completed. Replaces every `=== 'paused'`. */
export function isRoutineStopped(status?: string | null): boolean {
  return status === 'paused' || status === 'completed';
}

/** Ran out of scheduled dates: it cannot be resumed, only re-scheduled. */
export function isRoutineCompleted(status?: string | null): boolean {
  return status === 'completed';
}

/** The word a stopped routine wears. Callers render this only when stopped. */
export function routineStoppedLabel(status?: string | null): 'Paused' | 'Completed' {
  return status === 'completed' ? 'Completed' : 'Paused';
}
